import { ProtobufClient } from 'api/protobuf/protobuf-client';
import { goBackendApi } from 'hooks/api/httpApi';
import { mapEveryBigintIntoNumber } from 'utils/map-every-bigint-into-number';

import { OrderListResponseSchema } from '../../../pb/responses/v1/order_list_response_pb';

import type { Meta } from 'models/Meta';
import type { GetOrdersResponseBody, Order, OrdersQuery } from 'models/Order';

// GET /api/v1/orders — provider (and tags) are repeated query params (`provider[]=dpd&provider[]=kvikk`);
// passing an array through here as-is already serializes that way (axios's default array
// param handling), so no manual joining is needed for those two. paymentStatus is documented as a
// single string on this endpoint, unlike provider/tags, so multiple selections stay comma-joined.
// provider itself is optional — omitting it entirely fetches across every provider.
export const getOrders = async ({
    provider,
    pageSize,
    page,
    status,
    sort,
    search,
    tags,
    paymentStatus,
    startDate,
    endDate,
}: OrdersQuery): Promise<GetOrdersResponseBody> => {
    const normalizedStartDate = startDate && new Date(startDate);
    normalizedStartDate?.setHours(0, 0, 0, 0);

    const normalizedEndDate = endDate && new Date(endDate);
    normalizedEndDate?.setHours(23, 59, 59, 59);

    const pbClient = new ProtobufClient(goBackendApi());

    const response = await pbClient.getRequest(OrderListResponseSchema, '/api/v1/orders', {
        provider: provider ? (Array.isArray(provider) ? provider : [provider]) : undefined,
        sortColumn: sort?.column,
        sortDirection: sort?.order,
        status: status ? status : undefined,
        page,
        perPage: pageSize,
        search: search || undefined,
        tags: tags && tags.length > 0 ? tags : undefined,
        paymentStatus: paymentStatus && paymentStatus.length > 0 ? paymentStatus.join(',') : undefined,
        startDate: normalizedStartDate?.toISOString(),
        endDate: normalizedEndDate?.toISOString(),
    });

    // The response's OrderResponse/Meta messages already line up field-for-field with Order/Meta
    // (customerInfo.id is the only bigint on the wire), so normalize bigints then cast straight
    // through — the same pattern LabelService already uses for this same OrderResponse shape.
    const normalized = mapEveryBigintIntoNumber(response as unknown as Record<string, unknown>);

    return {
        orders: normalized.orders as unknown as Order[],
        meta: normalized.meta as unknown as Meta,
    };
};
